import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetPersonnelIdentityByIdResponse } from "src/api/Tmd.dtos";
import PersonnelIdentityContext from "src/contexts/data/PersonnelIdentityContext";
import usePersonnelIdentityById from "src/hooks/data/load/usePersonnelIdentityById";

/**
 * Configures the personnel identity request and the descendants receiving its state.
 */
interface IPersonnelProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetPersonnelIdentityByIdResponse,
        GetPersonnelIdentityByIdResponse
    >;
}

/**
 * Publishes the personnel identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const PersonnelIdentityProvider: FC<IPersonnelProviderProps> = ({
    children,
    id,
    configuration
}) => {
    const context = usePersonnelIdentityById(id, configuration);

    return (
        <PersonnelIdentityContext.Provider value={context}>
            {children}
        </PersonnelIdentityContext.Provider>
    );
};

/**
 * Loads personnel identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RoutePersonnelIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { personnelId } = useParams<{ personnelId: string }>();
    const id = personnelId ? parseInt(personnelId, 10) : undefined;

    return <PersonnelIdentityProvider id={id}>{children}</PersonnelIdentityProvider>;
};

export default PersonnelIdentityProvider;
